import React, { useState, useEffect } from 'react';
import { Paper, Text, Colors, TextButton } from 'library';
import { track } from 'shared';
import { Grid } from '@material-ui/core';
import { Provider } from 'models';
import { isLoggedIn } from 'api/UserApi';
import AuthModal from 'parent-portal/login/AuthModal';
import { useUserContext } from 'UserContext';
import ConnectPaymentModal from 'parent-portal/payments/Components/ConnectPaymentModal';
import { InstantBookJob as Booking } from 'models';
import CustomPagination from 'reusableComponents/customPagination';
import InstantBookConfirmModal from './InstantBookConfirmModal';
import base64 from 'base-64';
import { useHistory } from 'react-router';
import InstantAvailabilitySlot from './InstantAvailabilitySlot';

export interface IHardBooking {
    start_date: Date;
    end_date: Date;
}

export default function InstantAvailability({
    provider,
    external,
    bookedJob,
    setBookedJob,
    dateValue,
    availability,
}: {
    provider: Provider;
    bookedJob?: Booking;
    setBookedJob: (b: IHardBooking) => void;
    external?: boolean;
    dateValue: Date;
    availability: any;
}) {
    const [justBookedModalVisible, setJustBookedModalVisible] = useState<boolean>(false);
    const [showCompleteProfileDialog, setShowCompleteProfileDialog] = useState(false);
    const [tandemPayOpen, setTandemPayOpen] = useState<boolean>(false);
    const [pendingBooking, setPendingBooking] = useState<IHardBooking>();
    const [availabilitySlots, setAvailabilitySlots] = useState<IHardBooking[]>([]);
    const { user, refreshAlreadyLoggedInUser } = useUserContext();
    const history = useHistory();
    const pageSize = 3;

    function isPaymentSetup() {
        return user?.customer_id || (!!user?.payment_information && user?.payment_information.length > 0);
    }
    useEffect(availableTimes, [availability, dateValue]);

    function createTimeSlots(nums: number[]) {
        let numbs: number[][] = [];
        let start: number[] = [nums[0]];
        nums.forEach((num, index) => {
            if (index + 1 < nums.length && num === nums[index + 1] - 1) {
                start.push(nums[index + 1]);
            } else {
                numbs.push(start);
                start = [];
            }
        });
        return numbs.map((n: number[]) => {
            let st = new Date(dateValue!.getTime());
            st.setHours(n[0]);
            let ed = new Date(dateValue!.getTime());

            ed.setHours(n.pop() || 0);
            /* the end time is the end of the availability slot, not the start of it */
            ed.setHours(ed.getHours() + 1);

            return { start_date: st, end_date: ed };
        });
    }

    function availableTimes() {
        let enabledTimes = Array.from(Array(23).keys());
        let disabledTimes: number[] = [];

        let newDateValue = dateValue;
        newDateValue.setHours(0);

        if (availability && newDateValue) {
            if (availability[newDateValue.getTime()]?.['t_06_09'] === false) {
                disabledTimes = disabledTimes.concat([0, 1, 2, 3, 4, 5, 6, 7, 8]);
            }
            if (availability[newDateValue.getTime()]?.['t_09_12'] === false) {
                disabledTimes = disabledTimes.concat([9, 10, 11]);
            }
            if (availability[newDateValue.getTime()]?.['t_12_15'] === false) {
                disabledTimes = disabledTimes.concat([12, 13, 14]);
            }
            if (availability[newDateValue.getTime()]?.['t_15_18'] === false) {
                disabledTimes = disabledTimes.concat([15, 16, 17]);
            }
            if (availability[newDateValue.getTime()]?.['t_18_21'] === false) {
                disabledTimes = disabledTimes.concat([18, 19, 20]);
            }
            if (availability[newDateValue.getTime()]?.['t_21_24'] === false) {
                disabledTimes = disabledTimes.concat([21, 22, 23]);
            }
        }
        return setAvailabilitySlots(createTimeSlots(enabledTimes.filter((t: number) => !disabledTimes.includes(t))));
    }

    function handleClick(booking: IHardBooking) {
        if (isLoggedIn()) {
            if (external && !isPaymentSetup() && user?.claimed) {
                setPendingBooking(booking);
                setTandemPayOpen(true);
            } else if (user?.claimed) {
                setJustBookedModalVisible(true);
                setBookedJob(booking);
                track('Open Instant Book Confirm Modal From Profile');
                setPendingBooking(undefined);
            } else {
                setShowCompleteProfileDialog(true);
            }
        } else {
            track('Signup Modal opened via instant book From Profile');
            setPendingBooking(booking);
        }
    }

    async function closeTPModal() {
        await refreshAlreadyLoggedInUser();
        if (pendingBooking && isPaymentSetup()) {
            setTandemPayOpen(false);
            handleClick(pendingBooking);
        } else {
            setTandemPayOpen(false);
        }
    }

    function goToPublicAvailability(provider: Provider) {
        const encodedAccessor = base64.encode(provider.user.id.toString());
        const path = `/provider_profile/availability/${encodedAccessor}`;

        history.push({
            pathname: path,
        });
    }

    return (
        <>
            <InstantBookConfirmModal
                visible={justBookedModalVisible}
                setVisible={setJustBookedModalVisible}
                onClose={() => {
                    setJustBookedModalVisible(false);
                }}
                providerName={provider.user.first_name}
                bookingId={bookedJob?.id ?? -1}
            />

            <AuthModal
                location="Third Party Signup"
                open={showCompleteProfileDialog}
                onComplete={() => {
                    setShowCompleteProfileDialog(false);
                }}
                onClose={() => {
                    setShowCompleteProfileDialog(false);
                }}
                redirect={false}
                signup
            />
            <Paper style={{ backgroundColor: Colors.lightGreen }}>
                <Grid container direction="column" style={{ marginBottom: 20 }}>
                    <Text variant="h2" bold>
                        Instant Book
                    </Text>
                    <Text variant="body2">
                        Send a priority booking request to {provider.user.first_name} for a job inside one of their self
                        identified availability slots below. We will adjust the times next!
                    </Text>
                </Grid>
                <>
                    {availabilitySlots.map((booking) => (
                        <InstantAvailabilitySlot booking={booking} onBook={handleClick} />
                    ))}
                </>
                <CustomPagination
                    pageCount={availabilitySlots.length / pageSize}
                    content={availabilitySlots}
                    pageSize={pageSize}
                />
                {external ? <TextButton onClick={() => goToPublicAvailability(provider)}>View more</TextButton> : <></>}
            </Paper>
            {external ? (
                <ConnectPaymentModal
                    isOpen={tandemPayOpen}
                    onClose={closeTPModal}
                    fromRoute={'public/provider_profile'}
                    subtitle="TandemPay is required to instant book providers. You can connect a bank account or credit card to pay Tandem providers. Pay is adjustable at the end of every job. You'll be notified to confirm."
                />
            ) : null}
        </>
    );
}
